import React, { useState, useEffect } from "react";
import _ from "lodash";
import dataImage from "~/images/data_illustration.png";
import submissionStore from "../Submissions/Events";
import XLSX from "xlsx";
import { Fields } from "../Submissions/shared/Fields";
import MixpanelService from "../utils/MixpanelService";

const ColumnMapping = (props) => {
  const fieldDescriptions = Fields[props.mappingType];
  // Combine backend field schema with front end field presentation
  const columnMappingFields = props.fieldMappingFields.map((column) => {
    const matchingFieldDescription = fieldDescriptions[column.name];
    return { ...matchingFieldDescription, ...column };
  });
  const fieldNames = columnMappingFields.map((column) => column.name);
  const requiredFields = _.filter(columnMappingFields, ["required", true]).map(
    (column) => column.name
  );

  const [fileAttached, setFileAttached] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState(fieldNames[0]);
  const [attachedColumns, setAttachedColumns] = useState([]);
  const [mappings, setMappings] = useState(
    Object.fromEntries(fieldNames.map((column) => [column, null]))
  );

  useEffect(() => {
    const subs = submissionStore.subscribe((value) => {
      handleFile(value);
      handleFiles(value);
    });
  });

  const canSubmit = () => {
    const columns = _.pick(mappings, requiredFields);

    return !_.some(columns, _.isEmpty);
  };

  const handleFile = (file) => {
    setFileAttached(true);
  };

  const handleFiles = (file) => {
    var reader = new FileReader();

    reader.onload = function (e) {
      const workbook = XLSX.read(e.target.result, { type: "binary" });
      const sheet = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheet];

      const data = XLSX.utils.sheet_to_json(worksheet, {
        raw: false,
        header: 1,
      });

      setAttachedColumns(data[0]);
    };

    reader.readAsBinaryString(file);
  };

  const submitMappings = () => {
    if (!canSubmit()) return;
    const MAPPING_KEYS = {
      pharmacy: "field_mappings",
      medical: "medical_field_mappings",
      pharmacy_encounter: "pharmacy_encounter_field_mappings",
      medical_encounter: "medical_encounter_field_mappings",
    };
    const key = MAPPING_KEYS[props.mappingType] || "field_mappings";

    fetch(props.submitPath, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
      },
      body: JSON.stringify({ [key]: mappings }),
    })
      .then((res) => res.json())
      .then(
        (data) => {
          location.href = props.path;
        },
        (error) => {
          location.reload();
        }
      );
  };

  const buildMappings = (field) => {
    setMappings({
      ...mappings,
      [selectedColumn]: field,
    });

    var position = fieldNames.indexOf(selectedColumn);

    if (fieldNames.length == position + 1) {
      setSelectedColumn(fieldNames[0]);
    } else {
      setSelectedColumn(fieldNames[position + 1]);
    }
  };

  const removeMapping = (field) => {
    _.findKey(mappings, (val) => val == field);

    setMappings({
      ...mappings,
      [_.findKey(mappings, (val) => val == field)]: null,
    });
  };

  const renderFieldName = (columnMapping) => {
    if (columnMapping.required) {
      return (
        <span>
          <span>{columnMapping.label}</span>
          <span style={{ color: "#f50057" }}>*</span>
        </span>
      );
    }
    return columnMapping.label;
  };

  function renderFields() {
    return columnMappingFields.map((columnMapping, i) => {
      return (
        <div
          key={i}
          className={
            selectedColumn == columnMapping.name
              ? "modal__body__columns__item__container modal__body__columns__item__container--selected"
              : "modal__body__columns__item__container"
          }
          onClick={() => {
            setSelectedColumn(columnMapping.name);
          }}
        >
          <div className="tooltip" style={{ top: "calc(100% + 4px)" }}>
            <div className="tooltip__container">
              <div className="tooltip__container__title">{columnMapping.title}</div>
              <div className="tooltip__container__subtitle">{columnMapping.description}</div>
            </div>
          </div>

          <div className="modal__body__columns__item">{renderFieldName(columnMapping)}</div>

          {mappings[columnMapping.name] && (
            <div
              className="tag tag--square tag--light-blue"
              onClick={() => {
                removeMapping(mappings[columnMapping.name]);
              }}
            >
              <div className="tag__with_icon_text">{mappings[columnMapping.name]}</div>
              <i className="solid solid-budicon-cross-sign" />
            </div>
          )}
        </div>
      );
    });
  }

  const renderMappingsForm = () => {
    return (
      <div>
        <div className="onboarding__content__form">
          <div className="onboarding__content__form__title">Map data fields</div>
          <p>
            Use the form below to indicate which fields in your sample data file correspond to the
            information that we collect. The fields in the column on the left represent the data
            elements that we collect. The fields in the column on the right represent the data
            elements in your sample file.
            <br />
            <br />
            Hover over a field in the left-hand column for a detailed description of information
            that we collect.
          </p>
          <div className="modal__body__columns__container">{renderFields()}</div>
          <div className="modal__body__fields__container">
            {attachedColumns &&
              _.difference(attachedColumns, _.values(mappings)).map((field, i) => (
                <div
                  key={i}
                  className="modal__body__fields__item__container"
                  onClick={() => {
                    buildMappings(field);
                  }}
                >
                  {field}
                </div>
              ))}
          </div>
          <div className="onboarding__content__form__actions onboarding__content__form__actions--margin-sm">
            <a href={props.path}>
              <div
                className="btn btn--outline btn--white"
                onClick={() => {
                  MixpanelService.track("esp_confirm_column_mapping_setup_cancelled");
                }}
              >
                Cancel and Return
              </div>
            </a>
            <div
              className={canSubmit() ? "btn" : "btn btn--disabled"}
              disabled={canSubmit() ? false : true}
              style={{ flexGrow: 1, marginLeft: 30 }}
              onClick={() => {
                MixpanelService.track("esp_confirm_column_mapping_setup_saved");
                submitMappings();
              }}
            >
              Save Column Mappings
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAttachForm = () => {
    return (
      <div>
        <input
          type="hidden"
          name="authenticity_token"
          value={document.querySelector('meta[name="csrf-token"]').getAttribute("content")}
        />
        <div className="onboarding__content__form">
          <div className="onboarding__content__form__image">
            <img src={dataImage} />
          </div>
          <div className="onboarding__content__form__title">Attach a data file</div>
          <p style={{ marginBottom: 30 }}>
            <strong style={{ color: "#0A162F" }}>NOTE: </strong>
            Please ensure the first row of your sample data file has column headers.
          </p>
          <AttachFile />
          <div className="onboarding__content__form__actions onboarding__content__form__actions--margin-sm">
            <a href={props.path}>
              <div
                className="btn btn--outline btn--white"
                onClick={() => {
                  MixpanelService.track("esp_column_mapping_setup_attach_cancelled");
                }}
              >
                Cancel and Return
              </div>
            </a>
            <button
              className={fileAttached ? "btn" : "btn btn--disabled"}
              style={{ flexGrow: 1, marginLeft: 30 }}
              disabled={fileAttached ? false : true}
              type="submit"
              onClick={() => {
                MixpanelService.track("esp_column_mapping_setup_attach_continue");
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  };

  return fileAttached ? renderMappingsForm() : renderAttachForm();
};

const SheetJSFT = [
  ".xlsx",
  ".xlsb",
  ".xlsm",
  ".xls",
  ".xml",
  ".csv",
  ".txt",
  ".ods",
  ".fods",
  ".uos",
  ".sylk",
  ".dif",
  ".dbf",
  ".prn",
  ".qpw",
  ".123",
  ".wb*",
  ".wq*",
  ".html",
  ".htm",
];

const AttachFile = (props) => {
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(0);

  const handleChange = (e) => {
    const attachments = e.target.files;

    if (attachments.length > 0) {
      MixpanelService.track("esp_column_mapping_setup_attachment_success");
    }

    broadcastFile(attachments[0]);

    setFiles([...files, ...attachments]);

    setSelected(selected + 1);
  };

  const broadcastFile = (file) => {
    submissionStore.attachedFiles(file);
  };

  const handleSelectedFile = (file, index) => {
    broadcastFile(file);
    setSelected(index + 1);
  };

  return (
    <div>
      <div className="draw__section__header">
        <div className="draw__section__title">
          Attach a sample 340B claims data file.
          <br />
          <br />
          Please ensure this file has the same fields as the claims data files you intend to upload
          to 340B ESP™
        </div>
        <div className="draw__section__caddy" style={{ marginLeft: 30 }}>
          <div
            className="btn btn--small"
            onClick={() => {
              MixpanelService.track("esp_column_mapping_setup_attachments");
              document.getElementById("submissionAttachments").click();
            }}
          >
            Attach
          </div>
          <input
            type="file"
            style={{ display: "none" }}
            className="form-control"
            id="submissionAttachments"
            accept={SheetJSFT}
            multiple={true}
            onChange={handleChange}
          />
        </div>
      </div>
    </div>
  );
};

export default ColumnMapping;
